'use client';

import { useState } from 'react';

export interface DuplicateCheckState {
  checking: boolean;
  checkedValue: string | null;
  available: boolean;
  error: boolean;
}

const INITIAL_STATE: DuplicateCheckState = { checking: false, checkedValue: null, available: false, error: false };

// 구매회원/영업회원 아이디·닉네임 공용 "중복확인" 훅 — 두 테이블을 가리지 않고 서버에서 검사한다.
export function useDuplicateCheck(type: 'id' | 'nickname') {
  const [state, setState] = useState<DuplicateCheckState>(INITIAL_STATE);

  const check = async (value: string) => {
    const v = value.trim();
    if (!v) return;
    setState(s => ({ ...s, checking: true, error: false }));
    try {
      const res = await fetch(`/api/account/check-availability?type=${type}&value=${encodeURIComponent(v)}`);
      const data = await res.json();
      if (res.ok) {
        setState({ checking: false, checkedValue: value, available: !!data.available, error: false });
      } else {
        setState(s => ({ ...s, checking: false, error: true }));
      }
    } catch {
      setState(s => ({ ...s, checking: false, error: true }));
    }
  };

  // 마지막으로 확인한 값과 현재 입력값이 같고, 그 결과가 사용가능일 때만 유효 — 확인 후 값을 다시
  // 바꾸면 재확인 전까지는 유효하지 않은 것으로 취급된다.
  const isValidFor = (value: string) => state.checkedValue === value && state.available;

  return { state, check, isValidFor };
}

export function DuplicateCheckButton({
  value, state, onCheck, label = '중복확인',
}: {
  value: string;
  state: DuplicateCheckState;
  onCheck: () => void;
  label?: string;
}) {
  const stale = state.checkedValue !== value;
  return (
    <div className="flex items-center gap-2 mt-1.5">
      <button type="button" onClick={onCheck} disabled={!value.trim() || state.checking}
        className="shrink-0 text-xs bg-sky-100 border border-sky-200 rounded px-3 py-1.5 text-sky-700 hover:bg-sky-200 disabled:opacity-50">
        {state.checking ? '확인 중...' : label}
      </button>
      {!stale && !state.error && (
        <span className={`text-xs ${state.available ? 'text-green-600' : 'text-red-500'}`}>
          {state.available ? '사용 가능합니다' : '이미 사용 중입니다'}
        </span>
      )}
      {state.error && <span className="text-xs text-red-500">확인에 실패했습니다</span>}
    </div>
  );
}
